// __ROUTE_DRAFTS__ (2026-10-03) Draft routes column of the Route Builder.
// A draft is speculative: jobs keep their real driver and route until Finalize.
import React, { useEffect, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableJobTile } from './JobTile';

const btn = (bg = 'var(--btn-secondary-bg)') => ({
  padding: '0.35rem 0.6rem', backgroundColor: bg, color: 'var(--text-primary)', border: 'none',
  borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold',
});

const fmtClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// The whole draft column is the drop target (not just the dashed box), so a
// job let go anywhere over the open draft lands in it.
function DraftBody({ draftId, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'draftzone:' + draftId, data: { type: 'draftzone', draftId } });
  return (
    <div ref={setNodeRef} style={{ flex: 1, overflow: 'auto', padding: '0.6rem', backgroundColor: isOver ? 'rgba(88,166,255,0.05)' : 'transparent' }}>
      {children(isOver)}
    </div>
  );
}

function DropBox({ active, empty, children }) {
  return (
    <div
      style={{
        minHeight: empty ? '90px' : '40px', padding: '0.4rem', borderRadius: 'var(--radius-md)',
        border: `2px dashed ${active ? 'var(--accent-primary)' : 'var(--border-default)'}`,
        backgroundColor: active ? 'rgba(88,166,255,0.08)' : 'transparent', transition: 'all 150ms ease',
      }}
    >
      {children}
      {empty && (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: '1rem 0.5rem' }}>
          Drag jobs here, or select tiles on the left and press Build route.
        </div>
      )}
    </div>
  );
}

function Timeline({ timeline, busy }) {
  if (busy) return <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', padding: '0.4rem 0' }}>Timing the route with traffic and stop times...</div>;
  if (!timeline) return null;
  if (!timeline.success) {
    return <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', padding: '0.4rem 0' }}>No timeline: {timeline.message || timeline.error || 'stops could not be routed'}.</div>;
  }
  const stops = timeline.stopsWithETAs || [];
  const late = stops.filter((s) => s.isWithinTimeWindow && s.isWithinTimeWindow.status === 'late').length;
  return (
    <div style={{ marginTop: '0.5rem', fontSize: '0.74rem' }}>
      <div style={{ color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
        {fmtClock(timeline.estimatedStartTime)} to <strong>{fmtClock(timeline.estimatedEndTime)}</strong>
        {timeline.totalDistanceKm != null ? ' · ' + Math.round(timeline.totalDistanceKm) + ' km' : ''}
        {timeline.totalDriveTime ? ' · drive ' + timeline.totalDriveTime : ''}
        {timeline.totalDwellTime ? ' · stops ' + timeline.totalDwellTime : ''}
        {late > 0 && <span style={{ color: 'var(--color-danger)', fontWeight: 'bold' }}>{' · ' + late + ' late'}</span>}
      </div>
      {stops.map((s, i) => {
        const w = s.isWithinTimeWindow;
        const isLate = w && w.status === 'late';
        return (
          <div key={(s.id || i) + '-' + i} style={{ display: 'flex', gap: '0.4rem', padding: '0.15rem 0', borderBottom: '1px solid var(--border-default)' }}>
            <span style={{ width: '1.4rem', color: 'var(--text-muted)' }}>{i + 1}</span>
            <span style={{ width: '4.2rem', color: isLate ? 'var(--color-danger)' : 'var(--text-primary)' }}>{s.arrivalFormatted || fmtClock(s.estimatedArrival)}</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={(s.place_name ? s.place_name + ' - ' : '') + (s.address || '') + (s.dwellReason ? '\nStop time: ' + s.dwellMinutes + ' min (' + s.dwellReason + ')' : '')}>
              {s.stop_type === 'pickup' ? 'P' : s.stop_type === 'delivery' ? 'D' : '•'} {s.place_name || (s.address || '').split(',')[0]}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>{s.dwellMinutes != null ? s.dwellMinutes + 'm' : ''}</span>
            {isLate && <span style={{ color: 'var(--color-danger)' }}>{w.message}</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function DraftsPanel({
  drafts, activeDraftId, onSelectDraft, renderOrder, loadsById, tileProps, timeline, timelineBusy,
  onNewDraft, onRename, onChangeDate, onOptimize, onFinalize, onDiscard, onRemoveLoad, busy,
}) {
  const active = drafts.find((d) => d.id === activeDraftId) || null;
  const [name, setName] = useState(active ? active.name : '');
  useEffect(() => { setName(active ? active.name : ''); }, [active && active.id, active && active.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const order = active ? renderOrder(active) : [];   // dnd ids in display order
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)', backgroundColor: 'var(--surface-1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 'bold' }}>Draft routes</span>
          <button onClick={onNewDraft} style={btn()}>+ New draft</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
          {drafts.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>No drafts yet. Nothing real changes until you finalize one.</span>}
          {drafts.map((d) => (
            <button
              key={d.id}
              onClick={() => onSelectDraft(d.id)}
              style={{
                ...btn(d.id === activeDraftId ? 'var(--accent-primary)' : 'var(--btn-secondary-bg)'),
                fontWeight: d.id === activeDraftId ? 'bold' : 'normal',
              }}
            >
              {d.name} ({d.loadIds.length})
            </button>
          ))}
        </div>
      </div>

      {active && (
        <DraftBody draftId={active.id}>{(isOver) => (<>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem' }}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => { if (name.trim() && name !== active.name) onRename(active, name.trim()); }}
              style={{ flex: 1, minWidth: 0, padding: '0.3rem 0.4rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
              aria-label="Draft name"
            />
            <input
              type="date"
              value={active.route_date || ''}
              onChange={(e) => onChangeDate(active, e.target.value)}
              style={{ padding: '0.3rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem' }}
              aria-label="Route day"
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.6rem' }}>
            <button onClick={() => onOptimize(active)} disabled={busy || active.loadIds.length === 0} style={btn('var(--accent-primary)')} title="Best order using traffic drive times, each job's stop times and time windows (pickups before drop-offs)">
              Optimize order
            </button>
            <button onClick={() => onFinalize(active)} disabled={busy || active.loadIds.length === 0} style={btn('var(--color-success)')}>
              Finalize...
            </button>
            <button onClick={() => onDiscard(active)} disabled={busy} style={btn('#7f8c8d')}>Discard</button>
          </div>
          <SortableContext id={'draft:' + active.id} items={order} strategy={verticalListSortingStrategy}>
            <DropBox active={isOver} empty={order.length === 0}>
              {order.map((dndId, i) => {
                const loadId = dndId.split(':').pop();
                const load = loadsById[loadId];
                if (!load) {
                  return (
                    <div key={dndId} style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.4rem', marginBottom: '0.5rem', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                      A job no longer on the board (finished or removed).{' '}
                      <button onClick={() => onRemoveLoad(active, loadId)} style={btn()}>Remove</button>
                    </div>
                  );
                }
                return (
                  <SortableJobTile
                    key={dndId}
                    id={dndId}
                    data={{ type: dndId.startsWith('job:') ? 'job' : 'draftItem', loadId, draftId: active.id }}
                    load={load}
                    seq={i + 1}
                    {...tileProps(load, { inDraft: active.id })}
                    rightSlot={dndId.startsWith('dl:') ? (
                      <button
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onRemoveLoad(active, loadId); }}
                        title="Take this job out of the draft (its real route is not touched)"
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}
                      >×</button>
                    ) : null}
                  />
                );
              })}
            </DropBox>
          </SortableContext>
          <Timeline timeline={timeline} busy={timelineBusy} />
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>
            Speculative: these jobs stay with their current driver and route until you press Finalize.
          </div>
        </>)}</DraftBody>
      )}
    </div>
  );
}
